<template>
  <div class="flashing my-3" v-show="isShow" :class="[fullWidth && '!w-full']">
    <label
      v-if="labelText"
      :for="fieldName"
      :class="labelClass"
      class="text-start text-normal text-brand-black-secondary font-normal"
    >
      <div class="w-full flex items-center justify-start px-4 py-2">
        <span>{{ $t(labelText) }}</span>
      </div>
      <HintTippy v-if="hint" :hint="hint"></HintTippy>
    </label>
  </div>
</template>

<script setup>
import useVisible from '@/composables/useVisible'
import axiosInstance from '@/services'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import { computed, onMounted, ref, toRef, useAttrs } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const attrs = useAttrs()
const i18n = useI18n()
const props = defineProps({
  hint: String,
  fullWidth: {
    type: Boolean,
    default: false
  },
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  rules: Array,
  showRules: Object,
  defaultValue: String,
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  dependentOn: String
})

const labelText = ref()
const fieldName = toRef(props, 'fieldName')
const { findShowRule, getBindObj } = useVisible()
const route = useRoute()

const isShow = computed(() => {
  if (props.showRules) {
    return findShowRule(
      props.showRules.any || props.showRules.all,
      props.showRules.any ? 'any' : 'all',
      getBindObj(route.params.serviceId)
    )
  } else {
    return true
  }
})

const buildEstablishmentFines = async () => {
  const globalStore = useGlobalStore()
  const formStore = useFormStore()

  const { immigrationDepartmentId, establishmentNumber, delegateCardSequence, delegateCardYear } =
    formStore.form.values

  const body = {
    serviceTransactionId: globalStore.startedService.serviceTransactionId,
    immigrationDepartmentId,
    establishmentNumber,
    delegateCardSequence,
    delegateCardYear
  }
  const response = await axiosInstance.post('/api/fines/draft/calculateEstabFines', body)

  if ('finesAmount' in response && +(response.finesAmount || 0) > 0) {
    /**
     * bad practice. this code is essentially a side-effect.
     * modifying the formStore.draft.request shouldn't be done here.
     * but the way our solution is structured prevents us from developing a more
     * elegant solution. put it here or call
     * /api/fines/draft/calculateEstabFines again somewhere else.
     */
    Object.assign(formStore.draft.request, {
      addViolationRequest: true,
      EstablishmentCardFinsAmount: response.finesAmount
    })

    return i18n.t('EstablishmentCardFinsMsg', { count: response.finesAmount / 100 })
  }
  formStore.draft.request.addViolationRequest = false

  return null
}

const builderFuncs = {
  buildEstablishmentFines
}

onMounted(async () => {
  if ('label_builder' in attrs) {
    labelText.value = await builderFuncs[attrs.label_builder]()
    return
  }
  labelText.value = props.label
})
</script>
<style scoped>
.flashing {
  animation: blink 0.5s step-end infinite alternate;
  background: rgba(255, 0, 0, 0.11);
  border: dashed 3px #ffffff;
  font-size: 14px;
}

@keyframes blink {
  50% {
    border-color: #ff0000;
    background: rgba(255, 0, 0, 0.01);
  }
}
</style>
